import {
  multiFactor,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
  type TotpSecret,
  type MultiFactorInfo,
  type MultiFactorResolver,
  type MultiFactorError,
} from 'firebase/auth'
import { auth } from '@/config/firebase'

// Check if demo mode is enabled
const isDemoMode = import.meta.env.VITE_DEMO_MODE === 'true'

const TOTP_ISSUER = 'Turtledove Cyber'

/**
 * TOTP (authenticator-app) multi-factor authentication.
 *
 * This wraps Firebase's native MFA APIs - there is no hosted UI and no
 * redirect. Every call here is an SDK request to identitytoolkit, the same
 * endpoint signInWithEmailAndPassword already uses. The shared secret is
 * generated and stored by Identity Platform (same trust boundary as the
 * password hashes); we only ever hold it long enough to render the QR code.
 *
 * Requires the project to be upgraded to "Firebase Authentication with
 * Identity Platform" with TOTP enabled as a second factor. Before that
 * upgrade the enrollment calls throw auth/operation-not-allowed, which the
 * callers surface as a toast.
 */

// Holds the secret between beginTotpEnrollment() and confirmTotpEnrollment().
let pendingSecret: TotpSecret | null = null

export interface TotpEnrollmentChallenge {
  /** otpauth:// URI to render as a QR code. */
  qrCodeUrl: string
  /** Base32 secret for manual entry when the user can't scan. */
  secretKey: string
}

export function useMfa() {
  function getEnrolledFactors(): MultiFactorInfo[] {
    if (isDemoMode || !auth.currentUser) return []
    return multiFactor(auth.currentUser).enrolledFactors
  }

  /** Whether the signed-in user's email is verified (MFA prerequisite). */
  function isEmailVerified(): boolean {
    if (isDemoMode) return true
    return auth.currentUser?.emailVerified ?? false
  }

  /** Re-fetch the current user so emailVerified / enrolledFactors are fresh. */
  async function refreshUser(): Promise<void> {
    if (isDemoMode || !auth.currentUser) return
    await auth.currentUser.reload()
  }

  function isTotpEnrolled(): boolean {
    return getEnrolledFactors().some(f => f.factorId === TotpMultiFactorGenerator.FACTOR_ID)
  }

  /**
   * Start enrollment: returns the QR/secret to show the user. Call
   * confirmTotpEnrollment() with a code from their authenticator to finish.
   */
  async function beginTotpEnrollment(): Promise<TotpEnrollmentChallenge> {
    if (isDemoMode) {
      throw new Error('Two-factor authentication is not available in demo mode.')
    }
    if (!auth.currentUser) {
      throw new Error('You must be signed in to set up two-factor authentication.')
    }

    // MFA enrollment requires a verified email. Reload in case it was
    // verified after this session started.
    if (!auth.currentUser.emailVerified) {
      await auth.currentUser.reload()
      if (!auth.currentUser?.emailVerified) {
        throw new Error(
          'Your email address must be verified before you can enable two-factor authentication.'
        )
      }
    }

    const mfaUser = multiFactor(auth.currentUser)
    const session = await mfaUser.getSession()
    pendingSecret = await TotpMultiFactorGenerator.generateSecret(session)

    const accountName = auth.currentUser.email ?? auth.currentUser.uid
    return {
      qrCodeUrl: pendingSecret.generateQrCodeUrl(accountName, TOTP_ISSUER),
      secretKey: pendingSecret.secretKey,
    }
  }

  /** Finish enrollment with a code from the user's authenticator app. */
  async function confirmTotpEnrollment(verificationCode: string): Promise<void> {
    if (!pendingSecret || !auth.currentUser) {
      throw new Error('Setup expired. Please start again.')
    }
    const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
      pendingSecret,
      verificationCode.trim()
    )
    await multiFactor(auth.currentUser).enroll(assertion, 'Authenticator app')
    pendingSecret = null
  }

  function cancelTotpEnrollment(): void {
    pendingSecret = null
  }

  /** Remove the user's own TOTP factor (self-service disable). */
  async function disableTotp(): Promise<void> {
    if (isDemoMode) {
      throw new Error('Two-factor authentication is not available in demo mode.')
    }
    if (!auth.currentUser) {
      throw new Error('You must be signed in.')
    }
    const totp = multiFactor(auth.currentUser).enrolledFactors.find(
      f => f.factorId === TotpMultiFactorGenerator.FACTOR_ID
    )
    if (!totp) return
    await multiFactor(auth.currentUser).unenroll(totp)
  }

  return {
    getEnrolledFactors,
    isEmailVerified,
    refreshUser,
    isTotpEnrolled,
    beginTotpEnrollment,
    confirmTotpEnrollment,
    cancelTotpEnrollment,
    disableTotp,
  }
}

// ============================================================
// Sign-in second-factor resolution (driven by useAuth.login)
// ============================================================

// The resolver handed to us when a password sign-in is challenged for a
// second factor. Lives at module scope so useAuth can hand off to the
// login page's code-entry step.
let activeResolver: MultiFactorResolver | null = null

export function isMfaRequiredError(error: unknown): error is MultiFactorError {
  return (error as { code?: string } | undefined)?.code === 'auth/multi-factor-auth-required'
}

export function captureMfaResolver(error: MultiFactorError): void {
  activeResolver = getMultiFactorResolver(auth, error)
}

export function hasPendingMfaChallenge(): boolean {
  return activeResolver !== null
}

/** Complete a challenged sign-in with a TOTP code. Returns the signed-in user. */
export async function completeMfaSignIn(verificationCode: string) {
  if (!activeResolver) {
    throw new Error('No pending verification. Please sign in again.')
  }
  const hint =
    activeResolver.hints.find(h => h.factorId === TotpMultiFactorGenerator.FACTOR_ID) ??
    activeResolver.hints[0]
  const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, verificationCode.trim())
  const credential = await activeResolver.resolveSignIn(assertion)
  activeResolver = null
  return credential.user
}

export function clearMfaResolver(): void {
  activeResolver = null
}
